import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onSelect: (value: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Optional grouping function - returns group key for an item */
  getItemGroup?: (option: ComboboxOption) => string;
  /** Optional group label formatter */
  formatGroupLabel?: (groupKey: string) => string;
  /** Optional icon to show on the left of the trigger */
  leftIcon?: React.ReactNode;
}

export function Combobox({
  options,
  value,
  onSelect,
  placeholder = 'Select item...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  disabled = false,
  className = 'w-[200px]',
  getItemGroup,
  formatGroupLabel = (key) => key,
  leftIcon,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const selectedOption = React.useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const groupedOptions = React.useMemo(() => {
    if (!getItemGroup) {
      return { ungrouped: options };
    }

    const groups: Record<string, ComboboxOption[]> = {};
    for (const option of options) {
      const groupKey = getItemGroup(option);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(option);
    }

    const sortedGroups: Record<string, ComboboxOption[]> = {};
    Object.keys(groups)
      .sort()
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [options, getItemGroup]);

  const handleSelect = (optionValue: string) => {
    onSelect(optionValue === value ? null : optionValue);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-8 justify-between border-[#2E3835] text-sm hover:bg-gray-600/30 border-white/20 bg-gray-700/20',
            className
          )}
          disabled={disabled}
        >
          <div className="flex flex-1 items-center gap-2 truncate text-left">
            {leftIcon && <div className="shrink-0">{leftIcon}</div>}
            <span className="truncate">
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {value && !disabled && (
              <div
                className="rounded-sm p-0.5 hover:bg-white/10"
                onMouseDown={handleClear}
              >
                <X className="h-3 w-3 opacity-70" />
              </div>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', className)}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {Object.entries(groupedOptions).map(([groupKey, items]) => (
              <CommandGroup
                key={groupKey}
                heading={getItemGroup ? formatGroupLabel(groupKey) : undefined}
              >
                {items.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <span className="flex-1 truncate">{option.label}</span>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
